import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CURRENCIES } from "../../constants/currency";
import { DEFAULT_PLATFORMS } from "../../services/platformCatalog";
import { sanitizeNonNegativeInput } from "../../utils/nonNegativeInput";
import { comparePlatformNames } from "../simplified/simplifiedAccounts";
import {
  requiresOnboardingCurrencyChoice,
  resolveOnboardingDisplayCurrency,
} from "./onboardingPreferences";

const parseOptionalNumber = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const STEP_EXIT_MS = 190;

export default function OnboardingStepper({ open, onComplete }) {
  const [step, setStep] = useState(0);
  const [exitingStep, setExitingStep] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [motionDirection, setMotionDirection] = useState("forward");
  const [buyInMin, setBuyInMin] = useState("");
  const [buyInMax, setBuyInMax] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [displayCurrency, setDisplayCurrency] = useState(CURRENCIES.USD);
  const transitionTimeoutRef = useRef(null);

  const platformOptions = useMemo(
    () => [...DEFAULT_PLATFORMS].sort(comparePlatformNames),
    []
  );
  const needsCurrencyStep = requiresOnboardingCurrencyChoice(platforms);
  const totalSteps = needsCurrencyStep ? 3 : 2;
  const minNumber = parseOptionalNumber(buyInMin);
  const maxNumber = parseOptionalNumber(buyInMax);
  const hasBuyInRange = minNumber != null || maxNumber != null;
  const hasValidBuyInRange =
    minNumber == null || maxNumber == null || minNumber <= maxNumber;
  const canContinueFromBuyIn = hasBuyInRange && hasValidBuyInRange;
  const canContinueFromPlatforms = platforms.length > 0;

  useEffect(() => {
    if (step >= totalSteps) {
      setStep(totalSteps - 1);
    }
  }, [step, totalSteps]);

  useEffect(() => () => {
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!needsCurrencyStep) {
      setDisplayCurrency(resolveOnboardingDisplayCurrency(platforms));
    }
  }, [needsCurrencyStep, platforms]);

  if (!open || typeof document === "undefined") return null;

  const togglePlatform = (platform) => {
    setPlatforms((current) =>
      current.includes(platform)
        ? current.filter((item) => item !== platform)
        : [...current, platform]
    );
  };

  const complete = () => {
    onComplete({
      buyInMin,
      buyInMax,
      platforms,
      displayCurrency: resolveOnboardingDisplayCurrency(
        platforms,
        displayCurrency
      ),
    });
  };

  const moveToStep = (nextStep) => {
    if (isTransitioning || nextStep === step) return;

    setMotionDirection(nextStep > step ? "forward" : "back");
    setExitingStep(step);
    setIsTransitioning(true);

    transitionTimeoutRef.current = window.setTimeout(() => {
      setStep(nextStep);
      setExitingStep(null);
      setIsTransitioning(false);
      transitionTimeoutRef.current = null;
    }, STEP_EXIT_MS);
  };

  const goNext = () => {
    if (step === 0 && !canContinueFromBuyIn) return;
    if (step === 1 && !canContinueFromPlatforms) return;
    if (step === totalSteps - 1) {
      complete();
      return;
    }
    moveToStep(step + 1);
  };

  const renderStep = (stepToRender, phase = "enter") => {
    if (stepToRender === 0) {
      return (
        <div key={`buyin-${phase}`} className={`onboarding-step onboarding-step--buyin onboarding-step--${phase}`}>
          <h2 className="onboarding-step__title">Qual buy-in você joga?</h2>
          <div className="onboarding-range">
            <div>
              <label className="field-label" htmlFor="onboarding-buyin-min" style={{ fontSize: "10px", fontWeight: "200" }}>
                Mínimo
              </label>
              <input
                id="onboarding-buyin-min"
                className="input-field"
                type="number"
                min="0"
                placeholder="0"
                value={buyInMin}
                onChange={(event) =>
                  setBuyInMin(sanitizeNonNegativeInput(event.target.value, 9))
                }
              />
            </div>
            <div>
              <label className="field-label" htmlFor="onboarding-buyin-max" style={{ fontSize: "10px", fontWeight: "200" }}>
                Máximo
              </label>
              <input
                id="onboarding-buyin-max"
                className="input-field"
                type="number"
                min="0"
                placeholder="Sem limite"
                value={buyInMax}
                onChange={(event) =>
                  setBuyInMax(sanitizeNonNegativeInput(event.target.value, 9))
                }
              />
            </div>
          </div>
          {!hasValidBuyInRange && (
            <div className="field-error">O máximo precisa ser maior que o mínimo.</div>
          )}
        </div>
      );
    }

    if (stepToRender === 1) {
      return (
        <div key={`platforms-${phase}`} className={`onboarding-step onboarding-step--platforms onboarding-step--${phase}`}>
          <h2 className="onboarding-step__title">Quais plataformas você joga?</h2>
          <div className="onboarding-platform-grid">
            {platformOptions.map((platform) => (
              <label key={platform} className="platform-option onboarding-platform-option">
                <input
                  type="checkbox"
                  checked={platforms.includes(platform)}
                  onChange={() => togglePlatform(platform)}
                />
                {platform}
              </label>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={`currency-${phase}`} className={`onboarding-step onboarding-step--currency onboarding-step--${phase}`}>
        <h2 className="onboarding-step__title">Qual moeda exibir?</h2>
        <div className="onboarding-currency-options">
          <button
            className={`onboarding-currency-option ${
              displayCurrency === CURRENCIES.USD ? "is-selected" : ""
            }`}
            type="button"
            onClick={() => setDisplayCurrency(CURRENCIES.USD)}
            style={{ fontSize: "12px", fontWeight: "600" }}
          >
            Dólar
          </button>
          <button
            className={`onboarding-currency-option ${
              displayCurrency === CURRENCIES.BRL ? "is-selected" : ""
            }`}
            type="button"
            onClick={() => setDisplayCurrency(CURRENCIES.BRL)}
            style={{ fontSize: "12px", fontWeight: "600"}}
          >
            Real
          </button>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="onboarding-backdrop" role="presentation">
      <section
        className={`onboarding-modal onboarding-modal--step-${step}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        <div className="onboarding-header">
          <div>
            <p className="onboarding-kicker">Configuração inicial</p>
            <h1 id="onboarding-title" className="onboarding-title">
              Preferências do jogador
            </h1>
          </div>
          <div className="onboarding-progress" aria-label={`Etapa ${step + 1} de ${totalSteps}`}>
            {Array.from({ length: totalSteps }).map((_, index) => (
              <span
                key={index}
                className={index <= step ? "is-active" : ""}
                aria-hidden="true"
              />
            ))}
          </div>
        </div>

        <div className={`onboarding-step-stage onboarding-step-stage--${motionDirection}`}>
          {exitingStep == null
            ? renderStep(step, "enter")
            : renderStep(exitingStep, "exit")}
        </div>

        <div className="onboarding-actions">
          {step > 0 && (
            <button
              className="btn-ghost"
              type="button"
              onClick={() => moveToStep(step - 1)}
              disabled={isTransitioning}
            >
              Voltar
            </button>
          )}
          <button
            className="btn-red btn-confirm-action"
            type="button"
            onClick={goNext}
            disabled={
              isTransitioning ||
              (step === 0 && !canContinueFromBuyIn) ||
              (step === 1 && !canContinueFromPlatforms)
            }
            style={{ fontWeight: "600" }}>
            {step === totalSteps - 1 ? "Concluir" : "OK"}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}
